import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, ShoppingBag, Package, TrendingUp, 
  AlertTriangle, ArrowUpRight, Clock, Users, 
  CreditCard, Banknote, QrCode, Camera, ClipboardList,
  ChevronRight, Boxes
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function POSAdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const token = localStorage.getItem('pos_admin_token');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/pos/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pos-admin-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Welcome to POS Admin</h1>
            <p className="text-slate-300 mt-1">Manage your store, products, and sales</p>
          </div>
          <div className="flex gap-3">
            <Link to="/pos/admin/ai-onboard">
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                <Camera className="w-4 h-4 mr-2" />
                AI Scan Product
              </Button>
            </Link>
            <Link to="/pos" target="_blank">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                Open POS Terminal
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full flex items-center">
              <Clock className="w-3 h-3 mr-1" /> Today
            </span>
          </div>
          <p className="text-sm text-gray-500">Today's Sales</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{(stats?.today?.sales || 0).toLocaleString('en-IN')}
          </p>
          <p className="text-xs text-gray-400 mt-1">{stats?.today?.orders || 0} orders</p>
        </div>

        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500">Monthly Sales</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{(stats?.month?.sales || 0).toLocaleString('en-IN')}
          </p>
          <p className="text-xs text-gray-400 mt-1">{stats?.month?.orders || 0} orders</p>
        </div>

        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-violet-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500">Total Products</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.total_products || 0}</p>
          <p className="text-xs text-gray-400 mt-1">{stats?.pos_only_products || 0} POS only</p>
        </div>

        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500">Avg Order Value</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{Math.round(stats?.today?.avg_order_value || 0).toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/pos/admin/products" className="bg-white rounded-xl p-5 border shadow-sm hover:border-amber-500 transition-colors group">
          <Package className="w-8 h-8 text-amber-600 mb-3" />
          <p className="font-semibold text-gray-900 group-hover:text-amber-600">Manage Products</p>
          <p className="text-sm text-gray-500">Add, edit, push online</p>
        </Link>
        
        <Link to="/pos/admin/purchase-orders" className="bg-white rounded-xl p-5 border shadow-sm hover:border-amber-500 transition-colors group">
          <ClipboardList className="w-8 h-8 text-blue-600 mb-3" />
          <p className="font-semibold text-gray-900 group-hover:text-amber-600">Purchase Orders</p>
          <p className="text-sm text-gray-500">Create & manage POs</p>
        </Link>
        
        <Link to="/pos/admin/inventory" className="bg-white rounded-xl p-5 border shadow-sm hover:border-amber-500 transition-colors group">
          <Boxes className="w-8 h-8 text-violet-600 mb-3" />
          <p className="font-semibold text-gray-900 group-hover:text-amber-600">Inventory</p>
          <p className="text-sm text-gray-500">Stock management</p>
        </Link>
        
        <Link to="/pos/admin/ai-onboard" className="bg-white rounded-xl p-5 border shadow-sm hover:border-amber-500 transition-colors group">
          <Camera className="w-8 h-8 text-emerald-600 mb-3" />
          <p className="font-semibold text-gray-900 group-hover:text-amber-600">AI Product Scan</p>
          <p className="text-sm text-gray-500">Add products via camera</p>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Breakdown */}
        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Today's Payment Breakdown</h3>
          <div className="space-y-4">
            {stats?.payment_breakdown?.length > 0 ? (
              stats.payment_breakdown.map((payment) => {
                const config = {
                  CASH: { icon: Banknote, color: 'emerald', label: 'Cash' },
                  UPI: { icon: QrCode, color: 'violet', label: 'UPI' },
                  CARD: { icon: CreditCard, color: 'blue', label: 'Card' }
                }[payment._id] || { icon: DollarSign, color: 'gray', label: payment._id };
                
                const Icon = config.icon;
                const percentage = stats.today?.sales > 0 
                  ? ((payment.amount / stats.today.sales) * 100).toFixed(1)
                  : 0;
                
                return (
                  <div key={payment._id} className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg bg-${config.color}-100 flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 text-${config.color}-600`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">{config.label}</span>
                        <span className="text-sm text-gray-600">₹{payment.amount?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-${config.color}-500 rounded-full`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">No sales today</p>
            )}
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Stock Alerts</h3>
            <span className="text-sm text-red-600 bg-red-50 px-2 py-1 rounded-full">
              {stats?.out_of_stock_count || 0} out of stock
            </span>
          </div>
          
          <div className="space-y-3">
            {stats?.low_stock_products?.length > 0 ? (
              stats.low_stock_products.slice(0, 5).map((product) => (
                <div key={product.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                      {product.name}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-yellow-700">
                    {product.stock} left
                  </span>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No low stock alerts</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-xl p-6 border shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Recent POS Sales</h3>
          <Link to="/pos/admin/sales" className="text-amber-600 hover:text-amber-700 text-sm flex items-center">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500 border-b">
                <th className="pb-3 font-medium">Order #</th>
                <th className="pb-3 font-medium">Customer</th>
                <th className="pb-3 font-medium">Items</th>
                <th className="pb-3 font-medium">Payment</th>
                <th className="pb-3 font-medium">Total</th>
                <th className="pb-3 font-medium">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {stats?.recent_orders?.length > 0 ? (
                stats.recent_orders.map((order) => (
                  <tr key={order._id} className="text-sm">
                    <td className="py-3 font-medium">{order.order_number}</td>
                    <td className="py-3">{order.customer_phone}</td>
                    <td className="py-3">{order.items?.length || 0} items</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        order.payment_method === 'CASH' ? 'bg-emerald-100 text-emerald-700' :
                        order.payment_method === 'UPI' ? 'bg-violet-100 text-violet-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {order.payment_method}
                      </span>
                    </td>
                    <td className="py-3 font-semibold">₹{order.grand_total?.toFixed(2)}</td>
                    <td className="py-3 text-gray-500">
                      {new Date(order.created_at).toLocaleTimeString('en-IN', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
